import maplibregl, { type Map as MlMap } from 'maplibre-gl';
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';

/** Base map style. OpenFreeMap needs no API key; override with VITE_MAP_STYLE. */
const STYLE = import.meta.env.VITE_MAP_STYLE ?? 'https://tiles.openfreemap.org/styles/liberty';
const PRISHTINA: [number, number] = [21.1655, 42.6629];

/** Plain background used when the tile server cannot be reached, so editing still works. */
const FALLBACK_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {},
  layers: [{ id: 'background', type: 'background', paint: { 'background-color': '#eef2f6' } }],
};

const MapContext = createContext<MlMap | null>(null);
export const useMap = () => useContext(MapContext);

/** A MapLibre map; children render once the style has loaded and can use `useMap()`. */
export function MapView({
  children,
  className = 'h-[480px]',
  center = PRISHTINA,
  zoom = 12.5,
  fit,
}: {
  children?: ReactNode;
  className?: string;
  center?: [number, number];
  zoom?: number;
  /** Bounds to fit on load: [[west, south], [east, north]]. */
  fit?: [[number, number], [number, number]] | null;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MlMap | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the map is created once
  useEffect(() => {
    if (!container.current) return;
    const m = new maplibregl.Map({
      container: container.current,
      style: STYLE,
      center,
      zoom,
      attributionControl: { compact: true },
    });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    let loaded = false;
    m.on('load', () => {
      loaded = true;
      // Browser tests convert map coordinates to screen positions with this (dev server only).
      if (import.meta.env.DEV) (window as unknown as { __map?: MlMap }).__map = m;
      if (fit) m.fitBounds(fit, { padding: 40, duration: 0, maxZoom: 16 });
      setMap(m);
    });
    const fallback = () => {
      if (!loaded) m.setStyle(FALLBACK_STYLE);
    };
    m.once('error', fallback);
    const timer = setTimeout(fallback, 8000);
    return () => {
      clearTimeout(timer);
      setMap(null);
      m.remove();
    };
  }, []);

  return (
    <div className={`relative overflow-hidden rounded-lg border border-slate-200 ${className}`}>
      {/* MapLibre sets position:relative on this element, so size it explicitly (absolute would be overridden). */}
      <div ref={container} className="h-full w-full" />
      <MapContext.Provider value={map}>{map && children}</MapContext.Provider>
    </div>
  );
}

/** Bounds of all coordinates in nested GeoJSON coordinate arrays; null when there are none. */
export function boundsOf(...coords: unknown[]): [[number, number], [number, number]] | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  const visit = (c: unknown) => {
    if (Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number') {
      w = Math.min(w, c[0]);
      e = Math.max(e, c[0]);
      s = Math.min(s, c[1]);
      n = Math.max(n, c[1]);
    } else if (Array.isArray(c)) c.forEach(visit);
  };
  coords.forEach(visit);
  return Number.isFinite(w)
    ? [
        [w, s],
        [e, n],
      ]
    : null;
}
